import { Component, HostListener, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideHouse,
  lucideUsers,
  lucideStore,
  lucideBanknote,
  lucideLandmark,
  lucideBookOpen,
  lucideArrowLeftRight,
  lucideSettings,
  lucideMessageSquare,
  lucideSlidersHorizontal,
  lucideChevronDown,
  lucideChevronRight,
  lucideLogOut,
  lucideUser,
  lucideMenu,
  lucideX,
  lucideFileText,
  lucideKey,
  lucideWrench,
  lucideSearch,
  lucideLanguages,
  lucideBell,
  lucideCheck,
} from '@ng-icons/lucide';
import { Subject, debounceTime, distinctUntilChanged, of, switchMap, takeUntil } from 'rxjs';

import { ThemeToggleComponent } from '../../components/ui/theme-toggle/theme-toggle.component';
import { type MenuGroup } from '../../config/entity-config';
import { AuthService, type RoleAssignment } from '../../services/auth.service';
import { RoleAccessService } from '../../services/role-access.service';
import { SessionIdleService } from '../../services/session-idle.service';
import { environment } from '../../../environments/environment';
import { ShellRoutesService } from '../../services/shell-routes.service';
import {
  HeaderNotification,
  HeaderNotificationService,
} from '../../services/header-notification.service';
import { GlobalSearchResult, GlobalSearchService } from '../../services/global-search.service';
import { SupportedLanguage, UserPreferenceService } from '../../services/user-preference.service';
import { redirectMerchantUserToPortal } from '../../utils/merchant-portal';
import { SessionIdleModalComponent } from '../../components/session-idle-modal/session-idle-modal.component';

/**
 * Dashboard-styled layout: sidebar menu with collapsible groups, header with user dropdown.
 */
@Component({
  selector: 'app-dashboard-layout',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet, NgIcon, SessionIdleModalComponent, ThemeToggleComponent],
  templateUrl: './dashboard-layout.component.html',
  viewProviders: [
    provideIcons({
      lucideHouse,
      lucideUsers,
      lucideStore,
      lucideBanknote,
      lucideLandmark,
      lucideBookOpen,
      lucideArrowLeftRight,
      lucideSettings,
      lucideMessageSquare,
      lucideSlidersHorizontal,
      lucideChevronDown,
      lucideChevronRight,
      lucideLogOut,
      lucideUser,
      lucideMenu,
      lucideX,
      lucideFileText,
      lucideKey,
      lucideWrench,
      lucideSearch,
      lucideLanguages,
      lucideBell,
      lucideCheck,
    }),
  ],
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {
  /** Shared Tailwind class strings for the header dropdowns (tokens only, theme-safe). */
  protected readonly menuClass =
    'absolute top-[calc(100%+8px)] right-0 z-100 min-w-65 overflow-hidden rounded-[0.72rem] border border-border bg-card shadow-xl transition duration-150 starting:-translate-y-2 starting:opacity-0';
  protected readonly itemClass =
    'flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-3 text-left text-[0.9375rem] font-medium text-fg-secondary no-underline transition-colors hover:bg-surface-muted [&_ng-icon]:text-muted';
  protected readonly iconBtnClass =
    'inline-flex size-9 cursor-pointer items-center justify-center rounded-full border border-border bg-card text-fg-secondary transition-colors hover:border-primary/35 hover:bg-row-hover hover:text-primary';

  readonly roleAccess = inject(RoleAccessService);
  readonly auth = inject(AuthService);
  readonly shellRoutes = inject(ShellRoutesService);
  private readonly idle = inject(SessionIdleService);
  private readonly router = inject(Router);
  private readonly notificationService = inject(HeaderNotificationService);
  private readonly preferenceService = inject(UserPreferenceService);
  private readonly globalSearchService = inject(GlobalSearchService);
  private readonly destroy$ = new Subject<void>();
  private readonly searchQuery$ = new Subject<string>();

  /** Documentation URL from environment */
  readonly documentationUrl = environment.documentationUrl;

  /** Internal documentation URL from environment */
  readonly internalDocumentationUrl = environment.internalDocumentationUrl;

  /** Admin URL from environment */
  readonly adminUrl = environment.adminUrl;

  /** Track which menu group is expanded (only one at a time - accordion behavior) */
  readonly expandedGroup = signal<string | null>(null);

  /** Track sidebar collapsed state for mobile */
  readonly sidebarCollapsed = signal(false);

  /** Track user dropdown open state */
  readonly userDropdownOpen = signal(false);
  readonly roleDropdownOpen = signal(false);
  readonly notificationDropdownOpen = signal(false);
  readonly languageDropdownOpen = signal(false);
  readonly searchDropdownOpen = signal(false);
  readonly unreadNotificationCount = signal(0);
  readonly notifications = signal<HeaderNotification[]>([]);
  readonly selectedLanguage = signal<SupportedLanguage>('en');
  readonly searchResults = signal<GlobalSearchResult[]>([]);
  readonly searchTerm = signal('');

  readonly availableLanguages: Array<{ code: SupportedLanguage; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Francais' },
  ];

  ngOnInit(): void {
    this.idle.start();
    this.loadHeaderData();
    this.searchQuery$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((query) => {
          if (query.trim().length < 2) {
            return of({ results: [] as GlobalSearchResult[] });
          }
          return this.globalSearchService.search(query);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.searchResults.set(response.results);
      });
  }

  ngOnDestroy(): void {
    this.idle.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Close dropdown when clicking outside.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-dropdown-container')) {
      this.userDropdownOpen.set(false);
    }
    if (!target.closest('.role-dropdown-container')) {
      this.roleDropdownOpen.set(false);
    }
    if (!target.closest('.notification-dropdown-container')) {
      this.notificationDropdownOpen.set(false);
    }
    if (!target.closest('.language-dropdown-container')) {
      this.languageDropdownOpen.set(false);
    }
    if (!target.closest('.search-dropdown-container')) {
      this.searchDropdownOpen.set(false);
    }
  }

  /**
   * Toggle a menu group open/closed (accordion: opening one closes others).
   */
  toggleGroup(group: MenuGroup): void {
    const current = this.expandedGroup();
    this.expandedGroup.set(current === group.key ? null : group.key);
  }

  /**
   * Check if a group is expanded.
   */
  isExpanded(group: MenuGroup): boolean {
    return this.expandedGroup() === group.key;
  }

  /**
   * Toggle sidebar visibility (mobile).
   */
  toggleSidebar(): void {
    this.sidebarCollapsed.set(!this.sidebarCollapsed());
  }

  /**
   * Toggle user dropdown.
   */
  toggleUserDropdown(): void {
    this.userDropdownOpen.set(!this.userDropdownOpen());
  }

  toggleRoleDropdown(): void {
    this.roleDropdownOpen.set(!this.roleDropdownOpen());
  }

  toggleLanguageDropdown(): void {
    this.languageDropdownOpen.set(!this.languageDropdownOpen());
  }

  toggleNotificationDropdown(): void {
    const nextState = !this.notificationDropdownOpen();
    this.notificationDropdownOpen.set(nextState);
    if (nextState) {
      this.refreshNotifications();
    }
  }

  onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.searchDropdownOpen.set(value.trim().length > 0);
    this.searchQuery$.next(value);
  }

  openSearchResult(result: GlobalSearchResult): void {
    this.searchDropdownOpen.set(false);
    this.searchTerm.set('');
    this.router.navigateByUrl(result.route);
  }

  roleSwitchLabel(): string {
    const active = this.auth.user()?.active_assignment;
    if (!active || !active.role_code) return 'Role';
    return active.merchant_name ? `${active.role_code} - ${active.merchant_name}` : active.role_code;
  }

  roleAssignments(): RoleAssignment[] {
    return this.auth.user()?.role_assignments ?? [];
  }

  isActiveAssignment(assignment: RoleAssignment): boolean {
    const active = this.auth.user()?.active_assignment;
    return (
      active?.role_code === assignment.role_code &&
      (active?.merchant_id ?? null) === (assignment.merchant_id ?? null)
    );
  }

  switchAssignment(assignment: RoleAssignment): void {
    this.roleDropdownOpen.set(false);
    if (this.isActiveAssignment(assignment)) return;
    this.auth.switchActiveRole(assignment.role_code, assignment.merchant_id).subscribe((user) => {
      if (!user) return;
      if (redirectMerchantUserToPortal(user)) return;
      window.location.reload();
    });
  }

  isLanguageSelected(languageCode: SupportedLanguage): boolean {
    return this.selectedLanguage() === languageCode;
  }

  selectLanguage(languageCode: SupportedLanguage): void {
    this.languageDropdownOpen.set(false);
    if (this.selectedLanguage() === languageCode) return;
    this.preferenceService
      .updateLanguage(languageCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedLanguage.set(languageCode);
        const target = languageCode === 'fr' ? '/fr/' : '/';
        window.location.assign(target);
      });
  }

  markNotificationAsRead(notification: HeaderNotification): void {
    if (notification.is_read) return;
    this.notificationService
      .markRead(notification.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notifications.set(
          this.notifications().map((item) => {
            if (item.id === notification.id) {
              return { ...item, is_read: true };
            }
            return item;
          })
        );
        this.unreadNotificationCount.set(Math.max(this.unreadNotificationCount() - 1, 0));
      });
  }

  markAllNotificationsAsRead(): void {
    this.notificationService
      .markAllRead()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.notifications.set(this.notifications().map((item) => ({ ...item, is_read: true })));
        this.unreadNotificationCount.set(0);
      });
  }

  /**
   * Navigate to settings page.
   */
  goToSettings(): void {
    this.userDropdownOpen.set(false);
    this.router.navigate(['/settings']);
  }

  /**
   * Handle logout: clear local state, call backend, navigate to login.
   */
  handleLogout(): void {
    this.userDropdownOpen.set(false);
    this.auth.logout();
  }

  private loadHeaderData(): void {
    this.preferenceService
      .getPreferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe((preferences) => {
        this.selectedLanguage.set(preferences.preferred_language);
      });
    this.refreshNotifications();
  }

  private refreshNotifications(): void {
    this.notificationService
      .list()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.notifications.set(response.results);
        this.unreadNotificationCount.set(response.unread_count);
      });
  }
}
